// Talks to the CRM API. Every write carries an Idempotency-Key, so a retry or an offline replay can
// never do the same thing twice. Writes made with no signal are queued on the phone (IndexedDB) and
// replayed in order when it's back in range, the same way the RPM app works.
import { createStore, del, get, keys, set } from "idb-keyval";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: any) { super(message); }
}

const outboxStore = createStore("gcrm-outbox", "actions");
export interface QueuedAction { id: string; method: string; url: string; body?: unknown; headers: Record<string, string>; label: string; at: string; error?: string }

type Listener = () => void;
const listeners = new Set<Listener>();
export const onQueueChange = (fn: Listener) => { listeners.add(fn); return () => listeners.delete(fn); };
const changed = () => listeners.forEach((f) => f());

async function raw(method: string, url: string, body?: unknown, headers: Record<string, string> = {}) {
  const res = await fetch("/api" + url, {
    method, credentials: "same-origin",
    headers: { "x-crm": "1", ...(body !== undefined && !(body instanceof FormData) ? { "content-type": "application/json" } : {}), ...headers },
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
  });
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? "error", data?.message ?? `Request failed (${res.status})`, data?.details);
  return data;
}

export const api = {
  get: <T = any>(url: string) => raw("GET", url) as Promise<T>,
  /**
   * A write. `version` goes in If-Match. With `offline` set, a write that fails for lack of signal is
   * queued and replayed later instead of failing.
   */
  async send<T = any>(method: "POST" | "PATCH" | "PUT" | "DELETE", url: string, body?: unknown,
    opts: { version?: string; offline?: string; key?: string } = {}): Promise<T | { queued: true }> {
    const headers: Record<string, string> = { "idempotency-key": opts.key ?? crypto.randomUUID() };
    if (opts.version) headers["if-match"] = opts.version;
    try {
      return await raw(method, url, body, headers);
    } catch (err) {
      const offline = err instanceof TypeError || !navigator.onLine;
      if (offline && opts.offline) {
        const action: QueuedAction = { id: headers["idempotency-key"], method, url, body, headers, label: opts.offline, at: new Date().toISOString() };
        await set(action.id, action, outboxStore);
        changed();
        return { queued: true };
      }
      throw err;
    }
  },
  post: <T = any>(url: string, body?: unknown, o?: { offline?: string; key?: string }) => api.send<T>("POST", url, body ?? {}, o),
  patch: <T = any>(url: string, body: unknown, version?: string, o?: { offline?: string }) => api.send<T>("PATCH", url, body, { ...o, version }),
  put: <T = any>(url: string, body: unknown) => api.send<T>("PUT", url, body),
  del: <T = any>(url: string, body?: unknown) => api.send<T>("DELETE", url, body),
  upload: (url: string, form: FormData) => raw("POST", url, form, { "idempotency-key": crypto.randomUUID() }),
};

export const isQueued = (r: unknown): r is { queued: true } => !!r && typeof r === "object" && (r as any).queued === true;

export async function queuedActions(): Promise<QueuedAction[]> {
  const ids = await keys(outboxStore);
  const all = await Promise.all(ids.map((k) => get<QueuedAction>(k, outboxStore)));
  return (all.filter(Boolean) as QueuedAction[]).sort((a, b) => a.at.localeCompare(b.at));
}

let replaying = false;
/** Sends queued actions in the order they were made. The same Idempotency-Key makes a replay safe. */
export async function replayQueue(): Promise<{ sent: number; failed: number }> {
  if (replaying || !navigator.onLine) return { sent: 0, failed: 0 };
  replaying = true;
  let sent = 0, failed = 0;
  try {
    for (const a of await queuedActions()) {
      try {
        await raw(a.method, a.url, a.body, a.headers);
        await del(a.id, outboxStore);
        sent++;
      } catch (err) {
        if (err instanceof TypeError) break; // still offline
        // The server refused it (for example someone else changed the record first): keep it visible.
        await set(a.id, { ...a, error: (err as Error).message }, outboxStore);
        failed++;
      }
    }
  } finally {
    replaying = false;
    changed();
  }
  return { sent, failed };
}

export async function discardQueued(id: string) { await del(id, outboxStore); changed(); }

/** Signing out clears everything this phone kept: cached records and queued actions. */
export async function forgetDevice() {
  for (const k of await keys(outboxStore)) await del(k, outboxStore);
  if ("caches" in window) for (const name of await caches.keys()) await caches.delete(name);
  changed();
}
